import { useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Spinner } from '@fluentui/react';
import AffinityVideoTable from './AffinityVideoTable';
import AffinityVideoContentTable from './AffinityVideoContentTable';
import { useBoolean } from "@fluentui/react-hooks";
import ErrorComponent from '../../../../Util/ErrorComponent';
import MessageBarComponent from '../../../../Util/MessageBarComponent';
import FluentSelect from '../../../../Forms/FluentSelect';

const MAX_SELECTABLE = 8;

const AffinityVideos = ({ fieldName = 'featuredVideoList', ...props }) => {
  const { setFieldValue, values } = useFormikContext();
  const selectedVideos = values[fieldName] || [];
  const [selectedVideo, setSelectedVideo] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [showDeleteModal, { toggle: toggleDeleteModal }] = useBoolean(false);

  // dataOptions.Videos is the array of all available Videos
  const allVideos = values.dataOptions?.videos || [];
  const noEligibleData = !allVideos || allVideos.length === 0;
  const loading = useMemo(() => noEligibleData, [noEligibleData]);
  const resourceOptions = allVideos;

  const handleRemoveVideo = (item) => {
    if (!item || !item.contentId) return;
    const updated = selectedVideos.filter(c => c.contentId !== item.contentId);
    // Reassign order to be contiguous
    const reOrdered = updated.map((video, idx) => ({ ...video, order: idx + 1 }));
    setFieldValue(fieldName, reOrdered);
    setShowSuccess(true);
  };

  const mapNewContent = (id) => {
    if (!id) return;
    const item = resourceOptions.find(x => x.contentId === id.value);
    if (selectedVideos.some(c => c.contentId === item.contentId)) return; // Prevent duplicates
    item.order = selectedVideos.length + 1; // Assign order based on current length
    const newlist = [...selectedVideos, item];
    if (newlist.length > MAX_SELECTABLE) {
        setShowError(true);
        setErrorMsg(`You can only select up to ${MAX_SELECTABLE} Videos.`);
        return;
    }
    setFieldValue(fieldName, newlist);
    setShowAddVideo(false);
    setShowSuccess(true);
  };

  if (loading) return <Spinner className="absolute-center" label="Loading Videos..." />;

  return (
    <>
      <MessageBarComponent
        message="Videos updated. Remember to save."
        showMessage={showSuccess}
        setShowMessage={setShowSuccess}
      />
      <ErrorComponent
        errorMessage={errorMsg}
        showError={showError}
        setShowError={setShowError}
      />
      {showAddVideo && (
        <div className="max-800">
          <FluentSelect
            options={allVideos.map(c => ({ value: c.contentId, label: `${c.contentId} - ${c.title}` }))}
            onChange={mapNewContent}
            label='Eligible Videos'
            component={FluentSelect}
          />
          <label className="preview-upload-help">Search for Videos by Title or ID. Then select to add.</label>
        </div>
      )}
      <AffinityVideoContentTable
        content={selectedVideos}
        selectedContent={selectedVideo}
        setSelectedContent={setSelectedVideo}
        deleteOnClick={handleRemoveVideo}
        maxSelectable={MAX_SELECTABLE}
        showAddContent={showAddVideo}
        loading={loading}
        createOnClick={() => setShowAddVideo(true)}
        {...props}  
      />
      {/* Add DeleteContentModal if needed */}
    </>
  );
};

export default AffinityVideos;
